import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiHandle from "services/apiHandle";

// ==================== Async Thunks ====================

// Suggest a trip (Fleet Manager)
export const suggestTrip = createAsyncThunk(
  "trips/suggestTrip",
  async (body, { rejectWithValue }) => {
    try {
      const response = await apiHandle("/trips/suggest", "POST", body);
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to suggest trip";
      return rejectWithValue(message);
    }
  }
);

// Get all suggested trips (Fleet Manager)
export const getAllSuggestedTrips = createAsyncThunk(
  "trips/getAllSuggestedTrips",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiHandle("/trips", "GET");
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to fetch trips";
      return rejectWithValue(message);
    }
  }
);

// Get my trips (Driver) with optional status filter
export const getMyTrips = createAsyncThunk(
  "trips/getMyTrips",
  async (status = null, { rejectWithValue }) => {
    try {
      const query = status ? `?status=${status}` : "";
      const response = await apiHandle(
        `/trips/driver/my-trips${query}`,
        "GET"
      );
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to fetch my trips";
      return rejectWithValue(message);
    }
  }
);

// Get trip by ID (Fleet Manager)
export const getTripById = createAsyncThunk(
  "trips/getTripById",
  async (tripId, { rejectWithValue }) => {
    try {
      const response = await apiHandle(`/trips/${tripId}`, "GET");
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to fetch trip details";
      return rejectWithValue(message);
    }
  }
);

// Respond to trip (Driver: accept/reject)
export const respondToTrip = createAsyncThunk(
  "trips/respondToTrip",
  async ({ tripId, body }, { rejectWithValue }) => {
    try {
      const response = await apiHandle(
        `/trips/${tripId}/respond`,
        "PATCH",
        body
      );
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to respond to trip";
      return rejectWithValue(message);
    }
  }
);

// Complete trip (Driver)
export const completeTrip = createAsyncThunk(
  "trips/completeTrip",
  async (tripId, { rejectWithValue }) => {
    try {
      const response = await apiHandle(`/trips/${tripId}/complete`, "PATCH");
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to complete trip";
      return rejectWithValue(message);
    }
  }
);

// Update trip (Fleet Manager)
export const updateTrip = createAsyncThunk(
  "trips/updateTrip",
  async ({ tripId, body }, { rejectWithValue }) => {
    try {
      const response = await apiHandle(`/trips/${tripId}`, "PATCH", body);
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to update trip";
      return rejectWithValue(message);
    }
  }
);

// Delete trip (Fleet Manager)
export const deleteTrip = createAsyncThunk(
  "trips/deleteTrip",
  async (tripId, { rejectWithValue }) => {
    try {
      const response = await apiHandle(`/trips/${tripId}`, "DELETE");
      return { ...response.data, tripId };
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to delete trip";
      return rejectWithValue(message);
    }
  }
);

// ==================== Slice ====================

const tripsSlice = createSlice({
  name: "trips",
  initialState: {
    trips: [],
    currentTrip: null,
    loading: false,
    detailLoading: false,
    createLoading: false,
    respondLoading: false,
    completeLoading: false,
    error: null,
    message: null,
    showMessage: false,
  },
  reducers: {
    clearTripMessage: (state) => {
      state.message = null;
      state.showMessage = false;
      state.error = null;
    },
    setCurrentTrip: (state, action) => {
      state.currentTrip = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Suggest Trip
      .addCase(suggestTrip.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(suggestTrip.fulfilled, (state, action) => {
        state.createLoading = false;
        state.message = "Trip suggested successfully";
        state.showMessage = true;
        if (action.payload?.trip) {
          state.trips.unshift(action.payload.trip);
        }
      })
      .addCase(suggestTrip.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.showMessage = true;
      })

      // Get All Suggested Trips (Fleet Manager)
      .addCase(getAllSuggestedTrips.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllSuggestedTrips.fulfilled, (state, action) => {
        state.loading = false;
        state.trips = action.payload?.trips || action.payload || [];
      })
      .addCase(getAllSuggestedTrips.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get My Trips (Driver)
      .addCase(getMyTrips.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMyTrips.fulfilled, (state, action) => {
        state.loading = false;
        state.trips = action.payload?.trips || action.payload || [];
      })
      .addCase(getMyTrips.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Trip By ID
      .addCase(getTripById.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getTripById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.currentTrip = action.payload?.trip || action.payload;
      })
      .addCase(getTripById.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload;
      })

      // Respond to Trip
      .addCase(respondToTrip.pending, (state) => {
        state.respondLoading = true;
        state.error = null;
      })
      .addCase(respondToTrip.fulfilled, (state, action) => {
        state.respondLoading = false;
        state.message = "Trip response submitted";
        state.showMessage = true;
        // Update in list
        const updatedTrip = action.payload?.trip;
        if (updatedTrip) {
          const index = state.trips.findIndex(
            (t) => t._id === updatedTrip._id
          );
          if (index !== -1) {
            state.trips[index] = updatedTrip;
          }
          if (state.currentTrip?._id === updatedTrip._id) {
            state.currentTrip = updatedTrip;
          }
        }
      })
      .addCase(respondToTrip.rejected, (state, action) => {
        state.respondLoading = false;
        state.error = action.payload;
        state.showMessage = true;
      })

      // Complete Trip
      .addCase(completeTrip.pending, (state) => {
        state.completeLoading = true;
        state.error = null;
      })
      .addCase(completeTrip.fulfilled, (state, action) => {
        state.completeLoading = false;
        state.message = "Trip completed successfully";
        state.showMessage = true;
        const updatedTrip = action.payload?.trip;
        if (updatedTrip) {
          const index = state.trips.findIndex(
            (t) => t._id === updatedTrip._id
          );
          if (index !== -1) {
            state.trips[index] = updatedTrip;
          }
          if (state.currentTrip?._id === updatedTrip._id) {
            state.currentTrip = updatedTrip;
          }
        }
      })
      .addCase(completeTrip.rejected, (state, action) => {
        state.completeLoading = false;
        state.error = action.payload;
        state.showMessage = true;
      })

      // Update Trip
      .addCase(updateTrip.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTrip.fulfilled, (state, action) => {
        state.loading = false;
        state.message = "Trip updated successfully";
        state.showMessage = true;
        const updatedTrip = action.payload?.trip;
        if (updatedTrip) {
          const index = state.trips.findIndex(
            (t) => t._id === updatedTrip._id
          );
          if (index !== -1) {
            state.trips[index] = updatedTrip;
          }
        }
      })
      .addCase(updateTrip.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.showMessage = true;
      })

      // Delete Trip
      .addCase(deleteTrip.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTrip.fulfilled, (state, action) => {
        state.loading = false;
        state.message = "Trip deleted successfully";
        state.showMessage = true;
        state.trips = state.trips.filter(
          (t) => t._id !== action.payload.tripId
        );
      })
      .addCase(deleteTrip.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.showMessage = true;
      });
  },
});

export const { clearTripMessage, setCurrentTrip } = tripsSlice.actions;
export default tripsSlice.reducer;
